import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatCurrency, formatNumber } from '../lib/utils';
import { DollarSign, Clock, Users, FileText, Loader2, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#2A9D8F', '#264653', '#E9C46A', '#E76F51'];
const CHART_COLORS = { Client: '#2A9D8F', Partner: '#F97316' }; // Orange for Partner

// FY-ordered months (Apr-Mar for Indian FY)
const FY_MONTHS_ORDER = ['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'];
const CALENDAR_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [unpaidData, setUnpaidData] = useState([]);
  const [selectedFY, setSelectedFY] = useState('');
  const [selectedBillingMonth, setSelectedBillingMonth] = useState('');
  const [selectedPaymentMonth, setSelectedPaymentMonth] = useState('');
  const [selectedClientType, setSelectedClientType] = useState('');
  const [loading, setLoading] = useState(true);

  // Build filter params object - used by all data fetching functions
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (selectedFY && selectedFY !== 'all') params.financial_year = selectedFY;
    if (selectedBillingMonth && selectedBillingMonth !== 'all') params.billing_month = selectedBillingMonth;
    if (selectedPaymentMonth && selectedPaymentMonth !== 'all') params.payment_month = parseInt(selectedPaymentMonth);
    if (selectedClientType && selectedClientType !== 'all') params.client_type = selectedClientType;
    return params;
  }, [selectedFY, selectedBillingMonth, selectedPaymentMonth, selectedClientType]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API_URL}/dashboard/stats`, { params });
      setStats(response.data);
      
      // Set default FY if not set
      if (!selectedFY && response.data.financial_years?.length > 0) {
        setSelectedFY(response.data.financial_years[0]);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, selectedFY]);

  const fetchChartData = useCallback(async () => {
    try {
      const params = buildFilterParams();

      const [monthlyRes, currencyRes] = await Promise.all([
        axios.get(`${API_URL}/reports/monthwise-revenue`, { params }),
        axios.get(`${API_URL}/reports/currency-revenue`, { params })
      ]);
      
      // Format monthly data for chart
      const formattedMonthly = monthlyRes.data.map(item => ({
        month: getMonthShort(item.month),
        monthNum: item.month,
        Client: item.Client || 0,
        Partner: item.Partner || 0
      }));
      setMonthlyData(formattedMonthly);

      // Format currency data for pie chart
      const formattedCurrency = currencyRes.data.map(item => ({
        name: item.currency,
        value: item.total_paid_inr,
        count: item.count
      }));
      setCurrencyData(formattedCurrency);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    }
  }, [buildFilterParams]);

  const fetchUnpaidData = useCallback(async () => {
    try {
      const params = buildFilterParams();
      // Remove payment_month for unpaid data (unpaid invoices don't have payment dates)
      delete params.payment_month;

      const response = await axios.get(`${API_URL}/dashboard/unpaid-summary`, { params });
      setUnpaidData(response.data);
    } catch (error) {
      console.error('Failed to fetch unpaid data:', error);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    fetchUnpaidData();
  }, [fetchUnpaidData]);

  // When FY changes, optionally reset billing month
  const handleFYChange = (fy) => {
    setSelectedFY(fy);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFY('all');
    setSelectedBillingMonth('all');
    setSelectedPaymentMonth('all');
    setSelectedClientType('all');
  };

  const getMonthShort = (monthNum) => {
    const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum] || '';
  };

  // Get billing months in FY order when a FY is selected
  const getBillingMonthOptions = () => {
    if (selectedFY && selectedFY !== 'all') {
      return FY_MONTHS_ORDER;
    }
    return CALENDAR_MONTHS;
  };

  // Get payment months in FY order
  const getPaymentMonthOptions = () => {
    if (selectedFY && selectedFY !== 'all') {
      return [
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' }
      ];
    }
    return CALENDAR_MONTHS.map((m, idx) => ({ value: String(idx + 1), label: m }));
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return (selectedFY && selectedFY !== 'all') || 
           (selectedBillingMonth && selectedBillingMonth !== 'all') ||
           (selectedPaymentMonth && selectedPaymentMonth !== 'all') ||
           (selectedClientType && selectedClientType !== 'all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue || 0),
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Outstanding Amount',
      value: formatCurrency(stats?.outstanding_amount || 0),
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    },
    {
      title: 'Active Clients',
      value: stats?.active_clients || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Total Invoices',
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    }
  ];

  const billingMonthOptions = getBillingMonthOptions();
  const paymentMonthOptions = getPaymentMonthOptions();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your invoice and revenue tracking</p>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <span className="text-sm font-medium text-muted-foreground">Filters:</span>
          
          <Select value={selectedFY || "all"} onValueChange={handleFYChange}>
            <SelectTrigger className="w-44" data-testid="fy-selector">
              <SelectValue placeholder="Financial Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {stats?.financial_years?.map(fy => (
                <SelectItem key={fy} value={fy}>{fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedBillingMonth || "all"} onValueChange={setSelectedBillingMonth}>
            <SelectTrigger className="w-40" data-testid="billing-month-selector">
              <SelectValue placeholder="Billing Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Billing Months</SelectItem>
              {billingMonthOptions.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedPaymentMonth || "all"} onValueChange={setSelectedPaymentMonth}>
            <SelectTrigger className="w-40" data-testid="payment-month-selector">
              <SelectValue placeholder="Payment Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payment Months</SelectItem>
              {paymentMonthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedClientType || "all"} onValueChange={setSelectedClientType}>
            <SelectTrigger className="w-36" data-testid="client-type-selector">
              <SelectValue placeholder="Client Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Client">Client</SelectItem>
              <SelectItem value="Partner">Partner</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters() && (
            <button 
              onClick={clearFilters}
              className="text-sm text-primary hover:underline ml-2"
            >
              Clear all
            </button>
          )}

          {selectedFY && selectedFY !== 'all' && (
            <span className="text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded ml-auto">
              📅 Months ordered by FY (Apr-Mar)
            </span>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((card, index) => (
          <Card key={index} className="stat-card hover-lift" data-testid={`kpi-${card.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <p className="text-2xl font-bold mt-2 font-mono">{card.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${card.bgColor}`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Monthwise Revenue (Client vs Partner)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container">
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(value) => formatNumber(value)} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                    />
                    <Legend />
                    <Bar dataKey="Client" fill={CHART_COLORS.Client} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Partner" fill={CHART_COLORS.Partner} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data available for this period
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Currency Distribution Chart */}
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Currency Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container">
              {currencyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={currencyData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {currencyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data available for this period
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unpaid Invoices Table */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Unpaid Invoices Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unpaidData.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50">
                    <TableHead className="font-semibold">Client Name</TableHead>
                    <TableHead className="font-semibold">Billing Month</TableHead>
                    <TableHead className="text-right font-semibold">Outstanding Revenue (INR)</TableHead>
                    <TableHead className="text-center font-semibold">Invoices</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unpaidData.map((row, index) => (
                    <TableRow key={index} className="hover:bg-amber-50/50">
                      <TableCell className="font-medium">{row.client_name}</TableCell>
                      <TableCell>
                        {row.billing_month}
                        {row.billing_year && <span className="text-muted-foreground ml-1">({row.billing_year})</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-amber-600 font-semibold">
                        {formatCurrency(row.outstanding_revenue)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                          {row.invoice_count}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-amber-100/50 font-semibold">
                    <TableCell colSpan={2}>Total Outstanding</TableCell>
                    <TableCell className="text-right font-mono text-amber-700">
                      {formatCurrency(unpaidData.reduce((sum, row) => sum + row.outstanding_revenue, 0))}
                    </TableCell>
                    <TableCell className="text-center">
                      {unpaidData.reduce((sum, row) => sum + row.invoice_count, 0)} invoices
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 text-green-500 mb-3" />
              <p className="text-green-600 font-medium">No unpaid invoices!</p>
              <p className="text-sm">All invoices have been paid.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
